/**
 * OpenAI API entegrasyonu için servis dosyası
 * ChatGPT ile AI destekli içerik oluşturma
 *
 * GÜVENLİK: API Key frontend'te saklanmaz!
 * Firebase Cloud Functions üzerinden güvenli şekilde çağrı yapılır.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './config';

/**
 * Firebase Cloud Function ile AI içerik oluşturma (GÜVENLİ YÖ NTEM)
 * @param {string} prompt - Kullanıcının isteği
 * @param {Array} mediaFiles - Görsel/video dosyaları [{url: dataUrl, type: 'image'|'video', name: string}]
 * @param {Object} options - Ek ayarlar (model, max_tokens, vb.)
 * @returns {Promise<Object>} {success: boolean, text: string, error?: string}
 */
export const generateContentWithAI = async (prompt, mediaFiles = [], options = {}) => {
  try {
    console.log('🤖 AI içerik oluşturma başlatıldı (Firebase Function)');
    console.log('Prompt:', prompt);
    console.log('Medya sayısı:', mediaFiles.length);

    // Görselleri filtrele (sadece image tipindekiler)
    const imageFiles = mediaFiles.filter(m => m.type === 'image');

    if (mediaFiles.some(m => m.type === 'video')) {
      console.log('⚠️ Video dosyaları henüz desteklenmiyor, sadece görseller analiz edilecek');
    }

    console.log(`📷 ${imageFiles.length} görsel Firebase Function'a gönderiliyor`);

    // Firebase Callable Function'ı çağır
    const generateAIContent = httpsCallable(functions, 'generateAIContent');

    // Function'ı çağır
    const result = await generateAIContent({
      prompt: prompt,
      mediaFiles: imageFiles, // Sadece görselleri gönder
      options: {
        model: options.model,
        max_tokens: options.max_tokens || 1000,
        temperature: options.temperature || 0.7,
        top_p: options.top_p || 1,
        frequency_penalty: options.frequency_penalty || 0,
        presence_penalty: options.presence_penalty || 0,
        imageDetail: options.imageDetail || 'high'
      }
    });

    console.log('✅ Firebase Function yanıtı alındı');
    console.log('Model:', result.data.model);
    console.log('Token kullanımı:', result.data.usage);

    const generatedText = result.data.text;

    if (!generatedText) {
      console.error('❌ Function yanıtında metin bulunamadı');
      return {
        success: false,
        error: 'AI yanıt oluşturamadı. Lütfen tekrar deneyin.'
      };
    }

    console.log('📝 Oluşturulan metin:', generatedText.substring(0, 100) + '...');

    return {
      success: true,
      text: generatedText,
      usage: result.data.usage,
      model: result.data.model
    };

  } catch (error) {
    console.error('❌ AI içerik oluşturma hatası:', error);

    let errorMessage = 'Beklenmeyen bir hata oluştu';

    // Firebase Functions hata mesajları
    if (error.code === 'functions/unauthenticated') {
      errorMessage = '🔐 Oturum süreniz dolmuş. Lütfen yeniden giriş yapın.';
    } else if (error.code === 'functions/permission-denied') {
      errorMessage = '⛔ Bu işlem için yetkiniz yok.';
    } else if (error.code === 'functions/not-found') {
      errorMessage = '🔍 AI servisi bulunamadı. Lütfen sistem yöneticisine başvurun.';
    } else if (error.code === 'functions/deadline-exceeded') {
      errorMessage = '⏱️ İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage
    };
  }
};

/**
 * Platform spesifik AI prompt şablonları
 * @param {string} platformId - Platform ID (instagram, twitter, vb.)
 * @param {string} userPrompt - Kullanıcının özel isteği
 * @param {boolean} hasImages - Görseller var mı?
 * @returns {string} Optimize edilmiş prompt
 */
export const getPlatformOptimizedPrompt = (platformId, userPrompt, hasImages = false) => {
  const platformSpecs = {
    instagram: {
      maxChars: 2200,
      style: 'görsel odaklı, emoji kullanımlı, hashtag\'lerle zenginleştirilmiş',
      tips: 'Instagram için etkileşim odaklı, görseli anlatan ve emoji içeren bir metin yaz. En fazla 5-6 ana hashtag ekle (en popüler ve alakalı olanları seç).'
    },
    twitter: {
      maxChars: 280,
      style: 'kısa, özlü ve etkili',
      tips: 'Twitter için maksimum 280 karakter, dikkat çekici ve etkili bir tweet yaz. Maksimum 3-4 ana hashtag kullan.'
    },
    facebook: {
      maxChars: 63206,
      style: 'detaylı, hikaye anlatımlı',
      tips: 'Facebook için topluluk odaklı, konuşkan ve samimi bir metin yaz. Hashtag kullanımı minimumda (2-3 adet).'
    },
    linkedin: {
      maxChars: 3000,
      style: 'profesyonel, bilgilendirici',
      tips: 'LinkedIn için profesyonel, değer katacak ve iş odaklı bir içerik yaz. Maksimum 3-5 profesyonel hashtag kullan.'
    },
    tiktok: {
      maxChars: 2200,
      style: 'genç, eğlenceli, trend odaklı',
      tips: 'TikTok için genç kitlelere hitap eden, eğlenceli bir metin yaz. Maksimum 5-6 trend hashtag kullan.'
    },
    pinterest: {
      maxChars: 500,
      style: 'açıklayıcı, ilham verici',
      tips: 'Pinterest için görseli açıklayan, ilham verici bir açıklama yaz. Maksimum 3-4 arama dostu hashtag kullan.'
    },
    youtube: {
      maxChars: 5000,
      style: 'detaylı, SEO odaklı',
      tips: 'YouTube için açıklayıcı, anahtar kelime zengin bir açıklama yaz. Maksimum 5-6 SEO odaklı hashtag kullan.'
    }
  };

  const spec = platformSpecs[platformId] || platformSpecs.instagram;

  let enhancedPrompt = `${userPrompt}\n\n`;
  enhancedPrompt += `Platform: ${platformId.toUpperCase()}\n`;
  enhancedPrompt += `Stil: ${spec.style}\n`;
  enhancedPrompt += `Karakter Limiti: ${spec.maxChars} karakter\n`;
  enhancedPrompt += `${spec.tips}\n`;

  // Hashtag kuralı
  enhancedPrompt += '\n⚠️ ÖNEMLİ: Hashtag kullanırken:\n';
  enhancedPrompt += '- Maksimum 5-6 hashtag kullan\n';
  enhancedPrompt += '- Sadece en alakalı ve popüler hashtagleri seç\n';
  enhancedPrompt += '- Genel hashtagler yerine spesifik hashtagler kullan\n';
  enhancedPrompt += '- Hashtag spamı yapma\n';

  if (hasImages) {
    enhancedPrompt += '\nGörselleri analiz et ve içeriği görsellere uygun şekilde hazırla.';
  }

  return enhancedPrompt;
};

/**
 * Hızlı AI önerileri (kısa prompt'lar)
 */
export const quickPrompts = [
  {
    id: 'professional',
    title: '💼 Profesyonel',
    prompt: 'Bu görseller/konu için profesyonel ve ilgi çekici bir sosyal medya gönderisi yaz.'
  },
  {
    id: 'casual',
    title: '😊 Samimi',
    prompt: 'Bu görseller/konu için samimi ve doğal bir sosyal medya metni yaz.'
  },
  {
    id: 'funny',
    title: '😂 Eğlenceli',
    prompt: 'Bu görseller/konu için eğlenceli, espirili ve dikkat çekici bir metin yaz.'
  },
  {
    id: 'inspiring',
    title: '✨ İlham Verici',
    prompt: 'Bu görseller/konu için ilham verici ve motive edici bir metin yaz.'
  },
  {
    id: 'promotional',
    title: '🎯 Tanıtım',
    prompt: 'Bu görseller/ürün için satış odaklı, dikkat çekici ve harekete geçirici bir tanıtım metni yaz.'
  },
  {
    id: 'storytelling',
    title: '📖 Hikaye',
    prompt: 'Bu görseller/konu için ilgi çekici bir hikaye anlat.'
  }
];
